<template>
  <div
    class="conversation-list bg-gray-800 rounded-lg overflow-hidden h-full flex flex-col"
  >
    <div class="p-4 bg-gray-700 border-b border-gray-600">
      <h2 class="text-xl font-semibold text-white">Razgovori</h2>
    </div>

    <div
      v-if="showLoader"
      class="p-6 text-center text-gray-400 flex-grow flex items-center justify-center"
    >
      <div class="flex items-center">
        <svg
          class="animate-spin h-5 w-5 mr-2"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        Učitavanje...
      </div>
    </div>

    <div
      v-else-if="conversations.length === 0 && !isLoading"
      class="p-6 text-center text-gray-400 flex-grow flex flex-col items-center justify-center"
    >
      <p>Nemate aktivnih razgovora</p>
    </div>

    <div v-else class="flex flex-col flex-grow">
      <!-- Conversation items -->
      <div class="divide-y divide-gray-700 overflow-y-auto flex-grow">
        <conversation-item
          v-for="conversation in conversations"
          :key="`${conversation.otherUser.id}-${conversation.product.id}`"
          :conversation="conversation"
          :isActive="isActiveConversation(conversation)"
          @click="selectConversation(conversation)"
          @delete="showDeleteConfirmation(conversation)"
        />
      </div>

      <!-- Pagination -->
      <div
        v-if="meta && meta.totalPages > 1"
        class="p-3 bg-gray-700 border-t border-gray-600"
      >
        <pagination-component
          :meta="meta"
          :current-page="meta.page"
          :max-visible-pages="5"
          :scroll-to-top="false"
          @page-change="handlePageChange"
        />
      </div>
    </div>

    <!-- Toast notifikacija za potvrdu brisanja s dve opcije -->
    <div
      v-show="confirmToast.show"
      class="fixed inset-0 flex items-center justify-center z-50"
    >
      <div
        class="absolute inset-0 bg-black bg-opacity-30 backdrop-blur-sm"
        @click="closeConfirmToast"
      ></div>
      <div
        class="bg-gray-800 text-white rounded-lg shadow-xl border border-gray-700 p-5 max-w-md z-10 transform transition-all duration-300 border-l-4 border-l-yellow-500"
      >
        <div class="flex items-start">
          <!-- Warning icon -->
          <div class="flex-shrink-0 mr-4">
            <svg
              class="h-8 w-8 text-yellow-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>

          <div class="flex-1">
            <div class="flex justify-between items-start">
              <h3 class="text-lg font-medium mb-2 text-yellow-400">
                {{ confirmToast.title }}
              </h3>
              <button
                @click="closeConfirmToast"
                class="ml-4 text-gray-400 hover:text-gray-300 transition-colors"
              >
                <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fill-rule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
            </div>
            <p class="text-base text-gray-300">{{ confirmToast.message }}</p>

            <!-- Dugmad za akcije -->
            <div class="mt-4 flex justify-end space-x-3">
              <button
                @click="closeConfirmToast"
                class="px-4 py-2 rounded-md text-sm font-medium bg-gray-600 hover:bg-gray-700 text-white transition-colors"
              >
                Odustani
              </button>
              <button
                @click="handleConfirmDelete"
                class="px-4 py-2 rounded-md text-sm font-medium bg-red-600 hover:bg-red-700 text-white transition-colors"
              >
                Obriši
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  computed,
  reactive,
  ref,
  watch,
  onBeforeUnmount,
} from 'vue';
import { useStore } from 'vuex';
import { Conversation } from '@/types/chat-types';
import ConversationItem from './ConversationItem.vue';
import PaginationComponent from '@/components/PaginationComponent.vue';
import { debounce } from '../../utils/debounce';

export default defineComponent({
  name: 'ConversationList',
  components: {
    ConversationItem,
    PaginationComponent,
  },
  emits: ['select-conversation'],
  setup(props, { emit }) {
    const store = useStore();

    const conversations = computed(
      () => store.getters['chat/allConversations']
    );
    const meta = computed(() => store.getters['chat/conversationsMeta']);
    const currentConversation = computed(
      () => store.getters['chat/currentConversation']
    );
    const isLoading = computed(() => store.getters['chat/isLoading']);
    const showLoader = ref(false);

    // Debounced loader functions
    const debouncedShowLoader = debounce(() => {
      showLoader.value = true;
    }, 300);

    const hideLoaderImmediately = () => {
      debouncedShowLoader.cancel();
      showLoader.value = false;
    };

    // Watch loading state
    watch(isLoading, (loading) => {
      if (loading) {
        debouncedShowLoader();
      } else {
        hideLoaderImmediately();
      }
    });

    // Stanje za toast potvrdu brisanja
    const confirmToast = reactive({
      show: false,
      type: 'warning',
      title: 'Potvrda brisanja',
      message: 'Da li ste sigurni da želite obrisati ovu konverzaciju?',
      actionText: 'Obriši',
      conversation: null as Conversation | null,
    });

    // Load conversations for specific page
    const loadConversations = async (page: number) => {
      await store.dispatch('chat/fetchConversations', {
        page,
        limit: 4, // Use the same limit as in your API
      });
    };

    // Removed onMounted - let ChatView handle initial loading

    const isActiveConversation = (conversation: Conversation) => {
      if (!currentConversation.value) return false;

      return (
        currentConversation.value.otherUserId === conversation.otherUser.id &&
        currentConversation.value.productId === conversation.product.id
      );
    };

    const selectConversation = (conversation: Conversation) => {
      emit('select-conversation', {
        otherUserId: conversation.otherUser.id,
        productId: conversation.product.id,
      });
    };

    // Handle pagination page change
    const handlePageChange = (page: number) => {
      console.log('Changing to page:', page);
      loadConversations(page);
    };

    // Prikazuje toast za potvrdu brisanja
    const showDeleteConfirmation = (conversation: Conversation) => {
      confirmToast.conversation = conversation;
      confirmToast.show = true;
    };

    // Zatvara toast bez brisanja
    const closeConfirmToast = () => {
      confirmToast.show = false;
      confirmToast.conversation = null;
    };

    // Rukuje potvrdom brisanja
    const handleConfirmDelete = async () => {
      if (!confirmToast.conversation) return;

      try {
        const currentPage = meta.value?.page || 1;
        const totalPages = meta.value?.totalPages || 1;
        const currentTotal = meta.value?.total || 0;
        const currentLimit = meta.value?.limit || 4;

        await store.dispatch('chat/deleteConversation', {
          otherUserId: confirmToast.conversation.otherUser.id,
          productId: confirmToast.conversation.product.id,
        });

        // Calculate if we should go to previous page
        // New total after deletion
        const newTotal = currentTotal - 1;
        const newTotalPages = Math.ceil(newTotal / currentLimit);

        // If we're on the last page and it would become empty, go to previous page
        if (currentPage > newTotalPages && newTotalPages > 0) {
          console.log(
            `Going to previous page: ${currentPage} -> ${newTotalPages}`
          );
          loadConversations(newTotalPages);
        } else {
          console.log(`Staying on current page: ${currentPage}`);
          loadConversations(currentPage);
        }
      } catch (error) {
        console.error('Greška pri brisanju konverzacije:', error);
      } finally {
        confirmToast.show = false;
        confirmToast.conversation = null;
      }
    };

    onBeforeUnmount(() => {
      debouncedShowLoader.cancel();
    });

    return {
      conversations,
      meta,
      isLoading,
      showLoader,
      isActiveConversation,
      selectConversation,
      handlePageChange,
      confirmToast,
      showDeleteConfirmation,
      closeConfirmToast,
      handleConfirmDelete,
    };
  },
});
</script>
